// app\javascript\dashboard\store\modules\conversations\helpers.js
import { CONVERSATION_PRIORITY_ORDER } from 'shared/constants/messages';
import { matchesFilters } from './helpers/filterHelpers';

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

// Retrato mais velho não sobrescreve o mais novo. A resposta do POST e o
// `message.updated` do ActionCable correm em paralelo: no webchat (sem Meta no
// meio) o Webchat::SendOnWebchatService marca `delivered` ~0,3s depois de
// criar, e quando a resposta do POST (ainda `sent`, sem `webchat_kind`)
// chegava depois, a bolha voltava pra "enviando" até o próximo evento (conta
// 10, conv 280, msg 14773). Sem `updated_at` dos dois lados (mensagem
// otimista local) não há como ordenar, e vale o que chegou.
export const isStaleMessage = (current, incoming) => {
  if (!current?.updated_at || !incoming?.updated_at) return false;
  return Date.parse(incoming.updated_at) < Date.parse(current.updated_at);
};

export const filterByStatus = (chatStatus, filterStatus) =>
  filterStatus === 'all' ? true : chatStatus === filterStatus;

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = Number(teamId) === chatTeamId;
  return teamId ? isOnTeam && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};
export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

// Sem isto, qualquer evento em tempo real (assignee/status/etc de uma
// conversa qualquer) empurra a conversa pra allConversations, e a aba
// Menções — que não reaplica filtro de permissão/time (ver isMentionsTab em
// getters.js) — mostrava ela mesmo sem menção nenhuma. mentionedConversationIds
// é o livro-razão de quem realmente veio de um fetch/evento de menção.
export const filterByMention = (
  shouldFilter,
  conversationType,
  conversationId,
  mentionedConversationIds
) => {
  return conversationType === 'mention'
    ? shouldFilter && !!mentionedConversationIds?.has(conversationId)
    : shouldFilter;
};

// Mesmo fail-open da aba Menções, na aba Participando: o applyRoleFilter
// devolve true incondicional pra administrator/agent, então qualquer conversa
// empurrada por evento aparecia como "participando". O ledger registra só o
// que veio de um fetch de participação real; participação nova entra na
// próxima visita/refetch da aba (não há evento dedicado de participante).
export const filterByParticipation = (
  shouldFilter,
  conversationType,
  conversationId,
  participatingConversationIds
) => {
  return conversationType === 'participating'
    ? shouldFilter && !!participatingConversationIds?.has(conversationId)
    : shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  const {
    inboxId,
    status,
    labels = [],
    teamId,
    conversationType,
    mentionedConversationIds,
    participatingConversationIds,
  } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
  } = conversation;
  const team = meta.team || {};
  const { id: chatTeamId } = team;

  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );
  shouldFilter = filterByMention(
    shouldFilter,
    conversationType,
    conversation.id,
    mentionedConversationIds
  );
  shouldFilter = filterByParticipation(
    shouldFilter,
    conversationType,
    conversation.id,
    participatingConversationIds
  );

  return shouldFilter;
};

// Porta de entrada por EVENTO (WebSocket). Usa o MESMO predicado que o render
// usa pra decidir o que a lista mostra: conversa que não seria exibida NÃO
// entra em allConversations.
//
// Sem isso o array engordava com conversa invisível. O caso real: conversa de
// campanha nasce `pending` (Conversation#ensure_conversation_status, inbox com
// bot) e a IA resolve — com o atendente em "Abertas" ela era inserida, filtrada
// fora na renderização, e mesmo assim custava um objeto reativo e um
// filter+sort no array inteiro a cada evento seguinte. Foram 19.785 numa janela
// da conta 35, nenhuma delas jamais vista, e a aba travou.
//
// Trocar status/inbox/time/pasta refaz a lista do servidor
// (ChatList#resetAndFetchData → emptyAllConversations + fetch), então barrar
// aqui não esconde nada de forma permanente. A aba de atribuição
// (Minhas/Não atribuídas/Todos) fica DE FORA de propósito: ela troca sem
// esvaziar a lista, e barrar por ela sumiria com conversa viva.
//
// FALHA ABERTA: enquanto os filtros da página não chegaram (antes do primeiro
// setChatListFilters), a conversa ENTRA. Esconder conversa viva do atendente é
// pior que gastar memória.
export const shouldInsertConversationFromEvent = (conversation, _state) => {
  const {
    appliedFilters = [],
    conversationFilters = {},
    mentionedConversationIds,
    participatingConversationIds,
  } = _state;

  if (appliedFilters.length) {
    return matchesFilters(conversation, appliedFilters);
  }

  if (!conversationFilters.status) return true;

  return applyPageFilters(conversation, {
    ...conversationFilters,
    mentionedConversationIds,
    participatingConversationIds,
  });
};

/**
 * Filters conversations based on user role and permissions
 *
 * @param {Object} conversation - The conversation object to check permissions for
 * @param {string} role - The user's role (administrator, agent, etc.)
 * @param {Array<string>} permissions - List of permission strings the user has
 * @param {number|string} currentUserId - The ID of the current user
 * @param {Array<number|string>} currentUserTeamIds - Ids of every team the user belongs to
 * @param {boolean} teamsFetched - Whether the teams store has already loaded
 * @returns {boolean} - Whether the user has permissions to access this conversation
 */
export const applyRoleFilter = (
  conversation,
  role,
  permissions,
  currentUserId,
  currentUserTeamIds = [],
  teamsFetched = true
) => {
  // the role === "agent" check is typically not correct on it's own
  // the backend handles this by checking the custom_role_id at the user model
  // here however, the `getUserRole` returns "custom_role" if the id is present,
  // so we can check the role === "agent" directly
  if (['administrator', 'agent'].includes(role)) {
    return true;
  }

  // Filtro ortogonal, igual ao backend (Enterprise::ConversationPolicy):
  // resolvida escondida some independente da permissão de escopo.
  if (
    permissions.includes('hide_resolved_conversations') &&
    conversation.status === 'resolved'
  ) {
    return false;
  }

  // Check for full conversation management permission
  if (permissions.includes('conversation_manage')) {
    return true;
  }

  const conversationAssignee = conversation.meta.assignee;
  const isUnassigned = !conversationAssignee;
  const isAssignedToUser = conversationAssignee?.id === currentUserId;

  // Conversas dos times do agente + as atribuídas a ele, independente de time.
  // Mesma regra do backend (PermissionFilterService#filter_by_team e
  // ConversationPolicy). Sem o "as minhas", conversa atribuída a ele sem time
  // (Nova mensagem, template avulso, campanha) ou num time alheio vinha do
  // servidor e sumia da tela. Agente em N times vê os N; sem time, só as dele.
  if (permissions.includes('conversation_team_manage')) {
    // Não depende da lista de times — vale antes do guard abaixo.
    if (isAssignedToUser) return true;

    // Times ainda não carregados: o array vazio aqui significa "não sei", não
    // "nenhum time". Filtrar zeraria a lista em silêncio — a listagem que veio
    // do servidor JÁ está escopada, então ela passa e o console denuncia.
    if (!teamsFetched) {
      // eslint-disable-next-line no-console
      console.warn(
        '[conversations] Filtro de time adiado: a lista de times do agente ainda não carregou.'
      );
      return true;
    }

    const conversationTeamId = conversation.meta.team?.id;
    return (
      !!conversationTeamId &&
      currentUserTeamIds.some(teamId => Number(teamId) === conversationTeamId)
    );
  }

  // Check unassigned management permission
  if (permissions.includes('conversation_unassigned_manage')) {
    return isUnassigned || isAssignedToUser;
  }

  // Igual à anterior, mas a fila de não atribuídas é só a dos times do agente
  // (não atribuída sem time fica de fora). Mesma regra do backend
  // (Enterprise::Conversations::PermissionFilterService#filter_team_unassigned_and_mine).
  if (permissions.includes('conversation_team_unassigned_manage')) {
    if (isAssignedToUser) return true;
    if (!isUnassigned) return false;
    // Mesmo guard do conversation_team_manage acima: sem os times carregados
    // o array vazio significa "não sei", e a listagem já veio escopada.
    if (!teamsFetched) {
      // eslint-disable-next-line no-console
      console.warn(
        '[conversations] Filtro de time adiado: a lista de times do agente ainda não carregou.'
      );
      return true;
    }
    const conversationTeamId = conversation.meta.team?.id;
    return (
      !!conversationTeamId &&
      currentUserTeamIds.some(teamId => Number(teamId) === conversationTeamId)
    );
  }

  // Check participating conversation management permission
  if (permissions.includes('conversation_participating_manage')) {
    return isAssignedToUser;
  }

  return false;
};

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
};
const sortAscending = (valueA, valueB) => valueA - valueB;
const sortDescending = (valueA, valueB) => valueB - valueA;

const getSortOrderFunction = sortOrder =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.last_activity_at, b.last_activity_at),

  sortOnCreatedAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.created_at, b.created_at),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;

    return getSortOrderFunction(sortDirection)(p1, p2);
  },

  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    if (!a.waiting_since || !b.waiting_since) {
      if (!a.waiting_since && !b.waiting_since) {
        return sortFunc(a.created_at, b.created_at);
      }
      return sortFunc(a.waiting_since ? 0 : 1, b.waiting_since ? 0 : 1);
    }

    return sortFunc(a.waiting_since, b.waiting_since);
  },
};

export const sortComparator = (a, b, sortKey) => {
  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;
  return sortConfig[sortMethod](a, b, sortDirection);
};
